/**
 * Atalhos pt-BR para campos de data em NovoCasoPage (E13.F5.T05).
 *
 * Chips: Hoje, +7 dias, +30 dias e (opcional) Limpar. Operam sobre o
 * componente `input[type=date]` mantendo a digitação manual ainda
 * disponível para o usuário.
 */
import { ptBR } from '@/i18n/pt-BR';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function addDaysIso(baseIso: string, days: number): string {
  const base = baseIso === '' ? new Date() : new Date(`${baseIso}T00:00:00Z`);
  const next = new Date(base.getTime() + days * MS_PER_DAY);
  const yyyy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(next.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

interface DateShortcutsProps {
  testIdPrefix: string;
  disabled?: boolean | undefined;
  base: string;
  onSet: (iso: string) => void;
  showClear?: boolean | undefined;
  onClear?: (() => void) | undefined;
}

export function DateShortcuts({
  testIdPrefix,
  disabled,
  base,
  onSet,
  showClear,
  onClear,
}: DateShortcutsProps): JSX.Element {
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid={`${testIdPrefix}-shortcuts`}>
      <Chip
        testId={`${testIdPrefix}-today`}
        label={ptBR.casos.datepicker.today}
        disabled={disabled}
        onClick={() => onSet(addDaysIso('', 0))}
      />
      <Chip
        testId={`${testIdPrefix}-plus7`}
        label={ptBR.casos.datepicker.plus7}
        disabled={disabled}
        onClick={() => onSet(addDaysIso(base, 7))}
      />
      <Chip
        testId={`${testIdPrefix}-plus30`}
        label={ptBR.casos.datepicker.plus30}
        disabled={disabled}
        onClick={() => onSet(addDaysIso(base, 30))}
      />
      {showClear && onClear ? (
        <Chip
          testId={`${testIdPrefix}-clear`}
          label={ptBR.casos.datepicker.clear}
          disabled={disabled}
          onClick={onClear}
        />
      ) : null}
    </div>
  );
}

interface ChipProps {
  testId: string;
  label: string;
  disabled?: boolean | undefined;
  onClick: () => void;
}

function Chip({ testId, label, disabled, onClick }: ChipProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className="border border-border px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.04em] text-fg-dim transition-colors duration-fast ease-sc hover:bg-fg/[0.07] hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-50"
    >
      {label}
    </button>
  );
}
